import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ShieldCheck, ShieldOff, ShieldQuestion } from "lucide-react";

import type { ContactConsent } from "@/services/consent";

interface ConsentChipProps {
  consent?: ContactConsent | null;
  isLoading?: boolean;
}

type ChipState = "granted" | "revoked" | "unknown";

const stateConfig = {
  granted: { icon: ShieldCheck, color: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
  revoked: { icon: ShieldOff, color: "text-rose-600", bg: "bg-rose-500/10", border: "border-rose-500/20" },
  unknown: { icon: ShieldQuestion, color: "text-app-fg-muted", bg: "bg-app-surface", border: "border-app-border" },
} as const;

function formatDate(value: string): string {
  return format(new Date(value), "dd/MM/yyyy", { locale: ptBR });
}

function consentChipLabel(consent?: ContactConsent | null): { state: ChipState; label: string } {
  if (consent?.revoked_at) {
    return { state: "revoked", label: `Consentimento: revogado em ${formatDate(consent.revoked_at)}` };
  }
  if (consent?.granted && consent.granted_at) {
    return { state: "granted", label: `Consentimento: concedido em ${formatDate(consent.granted_at)}` };
  }
  return { state: "unknown", label: "Consentimento: não registrado" };
}

/** Read-only contact-consent chip for lead and patient profiles (W1-06). */
export function ConsentChip({ consent, isLoading }: ConsentChipProps) {
  if (isLoading) {
    return (
      <span className="inline-flex items-center rounded-full border border-app-border px-2.5 py-1 text-xs text-app-fg-muted">
        Consentimento: carregando…
      </span>
    );
  }

  const { state, label } = consentChipLabel(consent);
  const config = stateConfig[state];
  const Icon = config.icon;

  return (
    <span
      data-testid="consent-chip"
      data-state={state}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${config.border} ${config.bg} ${config.color}`}
    >
      <Icon className="size-3.5" aria-hidden="true" />
      {label}
    </span>
  );
}
